import ErrorBoundary from 'components/ErrorBoundary'
import { Routes as AppRoutes } from 'constants/index'
import { LoadingSpinner } from 'eezy-components'
import { updateDatalayer } from 'helpers/gtag'
import { useViewport } from 'helpers/hooks'
import {
  buildSnowplowUserStateContext,
  trackSnowplowPageView,
} from 'helpers/snowplow'
import cookie from 'js-cookie'
import FullWidthLayoutWrapper from 'layouts/full-width/FullWidthLayoutWrapper'
import { observer } from 'mobx-react'
import { useSnackbar } from 'notistack'
import OnboardingPopup from 'pages/setup/OnboardingPopup'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { RestfulProvider } from 'restful-react'
import Nav from './nav'
import UserResourceCountGetter from './nav/user_resource_count_getter'
import SessionLoader from './pages/auth/SignIn/SessionLoader'
import Router from './routes/Router'
import { AuthStore } from './stores'
import Api, { Events, Service, ServiceTypes } from './util/api'
import { useInstance } from './util/di'

const App = (): JSX.Element => {
  const { t } = useTranslation()
  const api = useInstance<Api>('api')
  const { session, clearSession } = useInstance<AuthStore>('AuthStore')
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const location = useLocation()
  const { isMobile } = useViewport()
  const snowplowInitialized = useRef(false)
  const lastTrackedSnowplowRoute = useRef<string | null>(null)

  const [appReady, setAppReady] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [snowplowReady, setSnowplowReady] = useState(false)
  const snowplowRoute = `${location.pathname}${location.search}${location.hash}`

  const isAdmin = useMemo(() => Boolean(session?.isAdmin), [session])
  const needsAccountSetup = useMemo(
    () => Boolean(!isAdmin && session && session?.needsAccountSetup),
    [session, isAdmin]
  )
  const needsOnboarding = useMemo(
    () => Boolean(!isAdmin && session && session?.needsOnboarding),
    [session, isAdmin]
  )
  useEffect(() => {
    if (session?.needsOnboarding) {
      const timer = setTimeout(() => setShowOnboarding(true), 2500)
      return () => clearTimeout(timer)
    } else {
      setShowOnboarding(false)
    }
  }, [session?.needsOnboarding])

  useEffect(() => {
    if (session && !api.services.length) {
      setAppReady(true)
      updateDatalayer({
        email_address: session.email,
        first_name: session.firstName,
        last_name: session.lastName,
        contributor_guid: session.id,
      })

      if (window.snowplow && !snowplowInitialized.current) {
        window.snowplow('newTracker', 'sp', 'a.vecteezy.com', {
          appId: 'content_manager_production',
          discoverRootDomain: true,
          eventMethod: 'beacon',
          cookieSameSite: 'Lax',
          context: {
            webPage: true,
            performanceTiming: true,
            gaCookies: true,
            geolocation: false,
          },
        })
        window.snowplow('enableActivityTracking', {
          minimumVisitLength: 10,
          heartbeatDelay: 10,
        })
        window.snowplow('enableLinkClickTracking')

        window.snowplow('addGlobalContexts', [buildSnowplowUserStateContext()])

        snowplowInitialized.current = true
        setSnowplowReady(true)
      }
    }
  }, [session, api.services])

  useEffect(() => {
    if (!snowplowReady || lastTrackedSnowplowRoute.current === snowplowRoute) {
      return
    }

    if (trackSnowplowPageView()) {
      lastTrackedSnowplowRoute.current = snowplowRoute
    }
  }, [snowplowReady, snowplowRoute])

  useEffect(() => {
    if (
      session &&
      !session.current_on_terms &&
      location.pathname !== AppRoutes.Terms &&
      !isAdmin &&
      !session.needsAccountSetup &&
      !session.needsOnboarding &&
      session.isApproved
    ) {
      navigate(AppRoutes.Terms)
    }

    if (
      session &&
      session?.hasFeature('directory_info_page') &&
      !session.directory_confirmation &&
      session.current_on_terms &&
      location.pathname !== AppRoutes.ContributorDirectoryInfo &&
      !isAdmin &&
      !session.needsAccountSetup &&
      !session.needsOnboarding &&
      session.isApproved
    ) {
      navigate(AppRoutes.ContributorDirectoryInfo)
    }

    if (
      needsAccountSetup &&
      location.pathname !== AppRoutes.Setup &&
      location.pathname !== AppRoutes.Profile
    ) {
      navigate(AppRoutes.Setup)
    }

    if (
      needsOnboarding &&
      location.pathname !== AppRoutes.PortfolioUpload &&
      location.pathname !== AppRoutes.PortfolioAddData &&
      location.pathname !== AppRoutes.Profile
    ) {
      navigate(AppRoutes.PortfolioUpload)
    }

    if (session && session.isNotAllowed) {
      clearSession()
      cookie.remove(`eezy-cm-auth-token`)

      enqueueSnackbar(t('util.auth.logged_out'), {
        variant: 'success',
        autoHideDuration: 1500,
      })
      navigate('/')
    }
  }, [
    session,
    location,
    isAdmin,
    navigate,
    needsAccountSetup,
    needsOnboarding,
    clearSession,
    enqueueSnackbar,
    t,
  ])

  useEffect(() => {
    const disposers = [
      api.emitter.subscribe(Events.NetworkError, ({ msg }) =>
        enqueueSnackbar(t(msg), { variant: 'error' })
      ),
    ]

    return () => disposers.forEach(disposer => disposer())
  }, [api, enqueueSnackbar, t])

  useEffect(() => {
    if (!session) {
      return
    }
    api.addService(
      new Service({
        key: ServiceTypes.CM,
        host: session.apiEndpoint,
        token: session.apiToken,
        isDefault: true,
      })
    )
  }, [api, session, session?.apiToken, session?.apiEndpoint])

  useEffect(() => {
    if (!session) {
      return
    }
    api.addService(
      new Service({
        key: ServiceTypes.Referrals,
        host: session.referralsUrl,
        token: session.referralsToken,
      })
    )
  }, [api, session, session?.referralsUrl, session?.referralsToken])

  if (!session) {
    const authToken = cookie.get(`eezy-cm-auth-token`)

    if (authToken) {
      return <SessionLoader apiToken={authToken} />
    }

    return (
      <ErrorBoundary>
        <Router session={session} />
      </ErrorBoundary>
    )
  }

  if (!appReady) {
    return (
      <FullWidthLayoutWrapper>
        <LoadingSpinner />
      </FullWidthLayoutWrapper>
    )
  }

  return (
    <RestfulProvider
      base={session.apiEndpoint}
      requestOptions={() => ({
        headers: { Authorization: 'Bearer ' + session.apiToken },
      })}
      queryParamStringifyOptions={{ arrayFormat: 'brackets' }}
    >
      <UserResourceCountGetter session={session}>
        <Nav session={session} isMobile={isMobile} />
        <ErrorBoundary>
          <Router session={session} />
          {session &&
          session.needsOnboarding &&
          showOnboarding &&
          session.state !== 'changes_required' ? (
            <OnboardingPopup />
          ) : null}
        </ErrorBoundary>
      </UserResourceCountGetter>
    </RestfulProvider>
  )
}

export default observer(App)
